import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { collection, collectionGroup, query, where, getDocs, doc, getDoc, updateDoc, deleteField, setDoc } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FileViewerModal from './FileViewerModal';
import {
  faMapMarkerAlt, 
  faCalendarCheck, 
  faRoute, 
  faEnvelope,
  faUser,
  faShare,
  faSignInAlt,
  faUserPlus,
  faLock,
  faComment,
  faMusic,
  faExclamationCircle,
  faUserShield,
  faPlus,
  faClock,
  faTicketAlt,
  faCheck,
  faExclamationTriangle,
  faUsers,
  faChevronDown,
  faChevronUp,
  faTimes,
  faCrown,
  faTools,
  faFilePdf, 
  faSpinner, 
  faUpload, 
  faTrash,
  faFileLines,
  faBuilding,
  faPenToSquare
} from '@fortawesome/free-solid-svg-icons';
import { 
  faInstagram, 
  faTwitter, 
  faFacebook, 
  faYoutube,
  faLinkedin
} from '@fortawesome/free-brands-svg-icons';
import UserContext from '../../context/UserContext';
import TourContext from '../../context/TourContext';
import TopBar from '../TopBar';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import ShareModal from '../common/ShareModal';
import styles from './BandProfile.module.css';
import defaultProfilePhoto from '../../assets/Images/defaultUserProfilePicture.png';
import defaultCoverPhoto from '../../assets/Images/defaultUserCoverPicture.png';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';
import spotifylogo from '../../assets/Images/spotifyicon.png';
import applemusiclogo from '../../assets/Images/applemusicicon.png';
import { sendBandMembershipRequest } from '../../utils/BandUtils';
import BandToursModal from '../common/BandToursModal'
import BandShowsModal from '../common/BandShowsModal'
import EPKBuilder from '../EPKSection/EPKBuilder';
import EPKViewer from '../EPKSection/EPKViewer';

// MembersSection component - Updated to hide admins from non-band members
const MembersSection = ({ bandData, isUserAdmin, isUserMember, navigate }) => {
  const [members, setMembers] = useState([]);
  const [crewMembers, setCrewMembers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const fetchMembers = async () => {
      if (!bandData?.id) return;
      
      setIsLoading(true);
      
      try {
        const db = getFirestoreInstance();
        const assignedMembersRef = collection(db, 'bands', bandData.id, 'assignedMembers');
        const membersSnapshot = await getDocs(assignedMembersRef);
        
        const artistMembers = [];
        const crew = [];
        const managementMembers = [];
        
        membersSnapshot.forEach(doc => {
          const memberData = doc.data();
          
          // Categorize based on role and userType
          if (memberData.userType === 'Manager' || memberData.role === 'Manager') {
            managementMembers.push(memberData);
          } else if (memberData.userType === 'Crew' || memberData.role === 'Crew') {
            crew.push(memberData);
          } else {
            artistMembers.push(memberData);
          }
        });
        
        setMembers(artistMembers);
        setCrewMembers(crew);
        setManagers(managementMembers);
      } catch (error) {
        console.error('Error fetching band members:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMembers();
  }, [bandData?.id]);
  
  // Return loading state if still loading
  if (isLoading) {
    return (
      <div className={styles.membersContainer}>
        <h3 className={styles.sectionTitle}>Band Members</h3>
        <div className={styles.loadingState}>Loading members...</div>
      </div>
    );
  }
  
  // Render artist members section
  const renderArtistMembers = () => {
    if (members.length === 0) return null;
    
    return (
      <div className={styles.memberTypeSection}>
        <h4 className={styles.memberTypeTitle}>Artists</h4>
        <div className={styles.membersGrid}>
          {members.map(member => (
            <div 
              key={member.userId} 
              className={styles.memberCard}
              onClick={() => navigate(`/${member.userName}`)}
            >
              <img 
                src={member.profilePictureUrl || defaultProfilePhoto} 
                alt={member.fullName} 
                className={styles.memberPhoto} 
              />
              <div className={styles.memberInfo}>
                <h4>{member.fullName}</h4>
                <span className={styles.memberBadge}>
                  <FontAwesomeIcon icon={faUser} /> {member.userSubType || 'Artist'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // Render crew members section
  const renderCrewMembers = () => {
    if (crewMembers.length === 0) return null;
    
    return (
      <div className={styles.memberTypeSection}>
        <h4 className={styles.memberTypeTitle}>Crew</h4>
        <div className={styles.membersGrid}>
          {crewMembers.map(member => (
            <div 
              key={member.userId} 
              className={styles.memberCard}
              onClick={() => navigate(`/${member.userName}`)}
            >
              <img 
                src={member.profilePictureUrl || defaultProfilePhoto} 
                alt={member.fullName} 
                className={styles.memberPhoto} 
              />
              <div className={styles.memberInfo}>
                <h4>{member.fullName}</h4>
                <span className={styles.crewBadge}>
                  <FontAwesomeIcon icon={faTools} /> {member.userSubType || 'Crew'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // Render management section
  const renderManagement = () => {
    if (managers.length === 0) return null;
    
    return (
      <div className={styles.memberTypeSection}>
        <h4 className={styles.memberTypeTitle}>Management</h4>
        <div className={styles.membersGrid}>
          {managers.map(member => (
            <div 
              key={member.userId} 
              className={styles.memberCard}
              onClick={() => navigate(`/${member.userName}`)}
            >
              <img 
                src={member.profilePictureUrl || defaultProfilePhoto} 
                alt={member.fullName} 
                className={styles.memberPhoto} 
              />
              <div className={styles.memberInfo}>
                <h4>{member.fullName}</h4>
                <span className={styles.managerBadge}>
                  <FontAwesomeIcon icon={faCrown} /> {member.userSubType || 'Manager'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };


 // Get actual member count
 const getMemberCount = () => {
  return members.length + crewMembers.length + managers.length;
};

return (
  <div className={styles.membersContainer}>
    <h3 className={styles.sectionTitle}>
      Band Members 
      <span className={styles.memberCount}>
        ({getMemberCount()})
      </span>
    </h3>
    
    {/* Render all sections */}
    {renderArtistMembers()}
    {renderCrewMembers()}
    {renderManagement()}
    
    {/* Empty state if no members */}
    {getMemberCount() === 0 && (
      <div className={styles.emptyState}>
        <p>No members yet</p>
      </div>
    )}
  </div>
);
};


// TourDates component for showing tour dates with filtering
const TourDates = ({ bandData, events }) => {
  const [filter, setFilter] = useState('all');
  const [displayDays, setDisplayDays] = useState(30);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Date formatting helper function
  const formatToDateString = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Generate dates for display
  const generateDates = () => {
    const datesList = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < displayDays; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      datesList.push(date);
    }
    return datesList;
  };

  // Check event status for a given date
  const checkEventStatus = (date) => {
    const dateString = formatToDateString(date);

    for (const event of events) {
      for (const eventDate of event.eventDates) {
        if (eventDate.cancelled) continue;

        const originalDate = formatToDateString(eventDate.date);
        const hasReschedule = eventDate.rescheduleDate?.isRescheduled;
        const rescheduledDate = hasReschedule ? 
          formatToDateString(eventDate.rescheduleDate.newDate) : null;

        if ((originalDate === dateString && !hasReschedule) || 
            (hasReschedule && rescheduledDate === dateString)) {
          return {
            isOnTour: true,
            eventName: event.type === 'tour' ? event.tourName : (event.showName || event.tourName),
            actName: event.actName,
            venue: eventDate.event?.venueDetails?.venueData,
            rescheduleInfo: hasReschedule && rescheduledDate === dateString ? {
              isRescheduled: true,
              originalDate: originalDate
            } : null
          };
        }
      }
    }

    return null;
  };

  // Filter dates based on selected filter
  const getFilteredDates = () => {
    if (filter === 'byDate') {
      return [selectedDate];
    }

    const allDates = generateDates();
    
    switch (filter) {
      case 'onTour':
        return allDates.filter(date => checkEventStatus(date));
      case 'available':
        return allDates.filter(date => !checkEventStatus(date));
      default:
        return allDates;
    }
  };

  // Render a date card
  const renderDateCard = (date) => {
    const eventStatus = checkEventStatus(date);
    const isOnTour = !!eventStatus;
  
    return (
      <div key={date.toISOString()} className={styles.dateCard}>
        <div className={styles.dateHeader}>
          <div className={styles.date}>
            {date.toLocaleDateString('en-GB', {
              weekday: 'short',
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            })}
          </div>
          <div className={`${styles.status} ${isOnTour ? styles.onTour : styles.available}`}>
            {isOnTour ? 'On Tour' : 'Available'}
          </div>
        </div>
  
        {isOnTour && eventStatus.venue && (
          <div className={styles.tourInfo}>
            <h4>{eventStatus.eventName}</h4>
            <p className={styles.actName}>{eventStatus.actName}</p>
            <div className={styles.venueInfo}>
              <p className={styles.venueName}>
                {eventStatus.venue.displayName}
              </p>
              <p className={styles.venueLocation}>
                {eventStatus.venue.formattedAddress}
              </p>
            </div>
            {eventStatus.rescheduleInfo && (
              <p className={styles.rescheduledInfo}>
                Rescheduled from: {new Date(eventStatus.rescheduleInfo.originalDate).toLocaleDateString()}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.datesContainer}>
      <div className={styles.dateFilters}>
        <button 
          className={`${styles.filterBtn} ${filter === 'all' ? styles.active : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button 
          className={`${styles.filterBtn} ${filter === 'onTour' ? styles.active : ''}`}
          onClick={() => setFilter('onTour')}
        >
          On Tour
        </button>
        <button 
          className={`${styles.filterBtn} ${filter === 'available' ? styles.active : ''}`}
          onClick={() => setFilter('available')}
        >
          Available
        </button>
        <button 
          className={`${styles.filterBtn} ${filter === 'byDate' ? styles.active : ''}`}
          onClick={() => {
            setFilter('byDate');
            setShowDatePicker(true);
          }}
        >
          By Date
        </button>
      </div>
  
      {filter === 'byDate' && (
        <input
          type="date"
          value={selectedDate.toISOString().split('T')[0]}
          onChange={(e) => setSelectedDate(new Date(e.target.value))}
          className={styles.datePicker}
        />
      )}
  
      <div className={styles.datesList}>
        {getFilteredDates().map(renderDateCard)}
      </div>
  
      {filter !== 'byDate' && displayDays < 365 && (
        <button 
          className={styles.loadMore}
          onClick={() => setDisplayDays(prev => prev + 30)}
        >
          Load More
        </button>
      )}
    </div>
  );
};

// Updated getActualMemberCount function to properly count band members
export const getActualMemberCount = async (bandId) => {
  if (!bandId) return 0;
  
  try {
    const db = getFirestoreInstance();
    const assignedMembersRef = collection(db, 'bands', bandId, 'assignedMembers');
    const membersSnapshot = await getDocs(assignedMembersRef);
    
    return membersSnapshot.size;
  } catch (error) {
    console.error('Error getting band member count:', error);
    return 0;
  }
};

// Updated function to get band's event counts from band_events collection
export const getBandEventCounts = async (bandId) => {
  try {
    const db = getFirestoreInstance();
    
    // Get all verified tour dates from band_events
    const eventDatesQuery = query(
      collectionGroup(db, 'eventDates'),
      where('verified', '==', true)
    );
    
    const eventDatesSnapshot = await getDocs(eventDatesQuery);
    
    // Count unique events (tours and shows) and total shows
    const uniqueTours = new Set();
    const uniqueShows = new Set();
    let showsCount = 0;
    
    eventDatesSnapshot.forEach(doc => {
      // Extract event ID and type from the document path
      const pathSegments = doc.ref.path.split('/');
      if (pathSegments.length >= 5) {
        const eventType = pathSegments[2]; // 'assignedEvents'
        const eventId = pathSegments[3]; // event ID
        const bandIdFromPath = pathSegments[1]; // band ID
        
        if (bandIdFromPath === bandId) {
          const dateData = doc.data();
          
          // Count based on event type
          if (dateData.eventType === 'tour') {
            uniqueTours.add(eventId);
          } else if (dateData.eventType === 'show') {
            uniqueShows.add(eventId);
          }
          
          // Count all verified dates as shows
          showsCount++;
        }
      }
    });
    
    return {
      toursCount: uniqueTours.size,
      showsCount: uniqueShows.size + showsCount // Total shows = individual shows + tour dates
    };
  } catch (error) {
    console.error('Error getting band event counts:', error);
    return {
      toursCount: 0,
      showsCount: 0
    };
  }
};

// Updated isCurrentlyOnTour function to check both tours and shows
const isCurrentlyOnTour = async (bandId) => {
  if (!bandId) return false;
  
  try {
    const db = getFirestoreInstance();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayString = today.toISOString().split('T')[0];
    
    // Check both tour and show events
    const eventTypes = ['tours', 'shows'];
    
    for (const collectionName of eventTypes) {
      // Query assigned users for this band
      const assignedUsersQuery = query(
        collectionGroup(db, 'assignedUsers'),
        where('id', '==', bandId)
      );
      
      const assignedUsersSnapshot = await getDocs(assignedUsersQuery);
      
      for (const userDoc of assignedUsersSnapshot.docs) {
        // Get the event ID from the path
        const pathSegments = userDoc.ref.path.split('/');
        if (pathSegments.length >= 3) {
          const eventId = pathSegments[1]; // The event ID
          
          // Query event dates for this event
          const eventDatesSnapshot = await getDocs(
            collection(db, collectionName, eventId, 'eventDates')
          );
          
          for (const dateDoc of eventDatesSnapshot.docs) {
            const dateData = dateDoc.data();
            
            // Normalize date format
            const eventDateString = dateData.date;
            
            if (eventDateString === todayString && !dateData.cancelled) {
              return true;
            }
          }
        }
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking if band is on tour:', error);
    return false;
  }
};

const BandEPKSection = ({ 
  bandData, 
  isOwnProfile, 
  handleEPKView, 
  handleEPKUpload, 
  handleEPKDelete,
  openEPKBuilder,
  customEPK
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size exceeds 5MB limit. Please select a smaller file.');
      return;
    }
    
    // Validate file type (only PDF)
    if (file.type !== 'application/pdf') {
      alert('Only PDF files are allowed.');
      return;
    }
    
    handleEPKUpload(file);
  };
  
  // Determine what to display based on band's EPK status
  const hasUploadedEPK = bandData?.epk;
  const hasCustomEPK = customEPK?.isActive === true;
  return (
  <div className={styles.membersContainer}>
  <h3 className={styles.sectionTitle}>Electronic Press Kit (EPK)</h3>
    {(hasUploadedEPK || hasCustomEPK) ? (
      // Band has an EPK - show the available EPK(s)
      <div className={styles.epkContainer}>
        {/* Display PDF EPK if available */}
        {hasUploadedEPK && (
          <div 
            className={styles.epkItem}
            onClick={() => handleEPKView(bandData.epk)}
          >
            <FontAwesomeIcon icon={faFilePdf} className={styles.epkIcon} />
            <div className={styles.epkInfo}>
              <h4 className={styles.epkName}>{bandData.epk.fileName || 'Press Kit'}</h4>
              <p className={styles.epkNote}>Click to view PDF</p>
            </div>
          </div>
        )}
        
        {/* Display custom built EPK if available */}
        {hasCustomEPK && (
          <div 
            className={`${styles.epkItem} ${styles.customEpkItem}`}
            onClick={() => openEPKBuilder('view')}
          >
            <FontAwesomeIcon icon={faFileLines} className={styles.epkIcon} />
            <div className={styles.epkInfo}>
              <h4 className={styles.epkName}>
                {customEPK.title || 'Custom Press Kit'}
                <span className={styles.customEpkBadge}>
                  <FontAwesomeIcon icon={faBuilding} />
                  Built with EPK Builder
                </span>
              </h4>
              <p className={styles.epkNote}>Click to view</p>
            </div>
          </div>
        )}
        
        {/* Owner actions for EPK management */}
        {isOwnProfile && (
          <div className={styles.epkActions}>
            <div className={styles.epkButtonGroup}>
              {hasUploadedEPK ? (
                <>
                  <button 
                    className={styles.uploadButton}
                    onClick={triggerFileInput}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={faUpload} />
                    )}
                    Replace PDF
                  </button>
                  
                  <button 
                    className={`${styles.uploadButton} ${styles.deleteButton}`}
                    onClick={() => handleEPKDelete(bandData.epk)}
                    disabled={isUploading}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                    Remove
                  </button>
                </>
              ) : (
                <button 
                  className={styles.uploadButton}
                  onClick={triggerFileInput}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <FontAwesomeIcon icon={faUpload} />
                  )}
                  Upload PDF EPK
                </button>
              )}
              
              {hasCustomEPK ? (
                <button 
                  className={styles.uploadButton}
                  onClick={() => openEPKBuilder('edit')}
                >
                  <FontAwesomeIcon icon={faPenToSquare} />
                  Edit Built EPK
                </button>
              ) : (
                <button 
                  className={styles.uploadButton}
                  onClick={() => openEPKBuilder('create')}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Create with EPK Builder
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    ) : (
      // No EPK yet - show options for owner, empty state for visitors
      isOwnProfile ? (
        <div className={styles.epkOptionsContainer}>
          <div className={styles.epkOptionItems}>
            <div 
              className={styles.epkOptionCard}
              onClick={triggerFileInput}
            >
              <div className={styles.epkOptionIcon}>
                <FontAwesomeIcon icon={faFilePdf} />
              </div>
              <h4>Upload PDF</h4>
              <p>Upload your existing press kit PDF</p>
            </div>
            
            <div 
              className={styles.epkOptionCard}
              onClick={() => openEPKBuilder('create')}
            >
              <div className={styles.epkOptionIcon}>
                <FontAwesomeIcon icon={faFileLines} />
              </div>
              <h4>EPK Builder</h4>
              <p>Create a professional EPK online</p>
            </div>
          </div>
          
          <p className={styles.epkOptionNote}>
            An Electronic Press Kit (EPK) helps event organizers, venues, and media learn about your band
          </p>
        </div>
      ) : (
        <div className={styles.emptyState}>
          <p>No press kit available</p>
        </div>
      )
    )}
    
    {/* Hidden file input for PDF upload */}
    <input
      type="file"
      accept=".pdf"
      ref={fileInputRef}
      style={{ display: 'none' }}
      onChange={handleFileChange}
    />
  </div>
  );
  };


const BandProfile = () => {
  const { bandUsername } = useParams();
  const navigate = useNavigate();
  const { userData, authToken } = useContext(UserContext);
  const { events, fetchEventsData } = useContext(TourContext);
  
  const [bandData, setBandData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState('showcase');
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isUserMember, setIsUserMember] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [posts, setPosts] = useState([]);
  const [bandEvents, setBandEvents] = useState([]);

  const eventsLoadedRef = useRef(false);
  const bandDataFetchedRef = useRef(false);

  const [bandStats, setBandStats] = useState({ toursCount: 0, showsCount: 0 });
  const [isOnTourStatus, setIsOnTourStatus] = useState(false);
  const [showToursModal, setShowToursModal] = useState(false);
  const [showShowsModal, setShowShowsModal] = useState(false);

  const [showPitchDeckModal, setShowPitchDeckModal] = useState(false);
  const [isPitchDeckUploading, setIsPitchDeckUploading] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  
  const [showEPKModal, setShowEPKModal] = useState(false);
const [isEpkUploading, setIsEpkUploading] = useState(false);
const [showEPKBuilder, setShowEPKBuilder] = useState(false);
const [epkBuilderMode, setEPKBuilderMode] = useState('create');
const [customEPK, setCustomEPK] = useState(null);


  // Fetch events when component loads
  useEffect(() => {
    if (userData && !eventsLoadedRef.current) {
      fetchEventsData(userData);
      eventsLoadedRef.current = true;
    }
  }, [userData, fetchEventsData]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch band data
  useEffect(() => {
    const fetchBandData = async () => {
      if (bandDataFetchedRef.current) return;
      
      setIsLoading(true);
      try {
        console.log("Fetching band with username:", bandUsername);
        const db = getFirestoreInstance();
        
        // Query bands collection by bandUserName
        const bandsQuery = query(
          collection(db, 'bands'),
          where('bandUserName', '==', bandUsername)
        );
        
        const querySnapshot = await getDocs(bandsQuery);
        
        if (querySnapshot.empty) {
          console.log("No band found with username:", bandUsername);
          setError('Band not found');
          setIsLoading(false);
          return;
        }
        
        const bandDoc = querySnapshot.docs[0];
        const data = {
          id: bandDoc.id,
          ...bandDoc.data()
        };
        
        console.log("Found band:", data);
        setBandData(data);
        bandDataFetchedRef.current = true;
        
        // Check user's relationship with the band if a user is logged in
        if (userData) {
          // Check if user is admin
          const isAdmin = data.members?.some(member => 
            member.userId === userData.userId && member.role === 'admin'
          );
          setIsUserAdmin(isAdmin);
          
          // Check if user is member (any role)
          const isMember = data.members?.some(member => 
            member.userId === userData.userId
          );
          setIsUserMember(isMember);
          
          // Check if user has pending request
          const hasPending = data.memberRequests?.some(req => 
            req.userId === userData.userId
          );
          setHasPendingRequest(hasPending);
        }
      } catch (error) {
        console.error('Error fetching band data:', error);
        setError('Error loading band profile');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchBandData();
  }, [bandUsername, userData]);

  useEffect(() => {
    // Fetch band event counts
    const fetchBandStats = async () => {
      if (bandData?.id) {
        const eventCounts = await getBandEventCounts(bandData.id);
        setBandStats(eventCounts);
        
        // Check if band is currently on tour
        const onTour = await isCurrentlyOnTour(bandData.id);
        setIsOnTourStatus(onTour);
      }
    };
    
    fetchBandStats();
  }, [bandData]);


  useEffect(() => {
    const processEvents = async () => {
      if (!bandData || !bandData.assignedEvents || bandData.assignedEvents.length === 0) {
        return;
      }
      
      try {
        const db = getFirestoreInstance();
        // Process multiple events (tours and shows)
        const eventPromises = bandData.assignedEvents.map(async (assignedEvent) => {
          const eventId = assignedEvent.eventId;
          const eventType = assignedEvent.eventType || 'tour';
          const event = events.find(e => e.id === eventId && e.type === eventType);
          
          if (event) {
            return {
              ...event,
              assignedAt: assignedEvent.assignedAt // Keep assignment date
            };
          } else {
            // If event not found in context, fetch it directly
            try {
              const collectionName = eventType === 'tour' ? 'tours' : 'shows';
              const eventDoc = await getDoc(doc(db, collectionName, eventId));
              
              if (eventDoc.exists()) {
                const eventData = eventDoc.data();
                
                // Fetch event dates
                const eventDatesSnapshot = await getDocs(
                  collection(db, collectionName, eventId, 'eventDates')
                );
                
                const eventDatesData = eventDatesSnapshot.docs.map(dateDoc => ({
                  id: dateDoc.id,
                  ...dateDoc.data(),
                }));
                
                return {
                  id: eventId,
                  type: eventType,
                  ...eventData,
                  eventDates: eventDatesData,
                  assignedAt: assignedEvent.assignedAt
                };
              }
            } catch (error) {
              console.error(`Error fetching ${eventType} data for ${eventId}:`, error);
              return null;
            }
          }
          return null;
        });
        
        // Wait for all events to be fetched
        const eventsData = await Promise.all(eventPromises);
        const validEvents = eventsData.filter(Boolean);
        
        // Sort events by most recently assigned
        const sortedEvents = validEvents.sort((a, b) => {
          const dateA = a.assignedAt ? new Date(a.assignedAt) : new Date(0);
          const dateB = b.assignedAt ? new Date(b.assignedAt) : new Date(0);
          return dateB - dateA; // Newest first
        });
        
        setBandEvents(sortedEvents);
      } catch (error) {
        console.error('Error processing event data:', error);
      }
    };
    
    if (bandData && events && events.length > 0) {
      processEvents();
    }
  }, [bandData, events]);


  useEffect(() => {
    return () => {
      bandDataFetchedRef.current = false;
      eventsLoadedRef.current = false;
    };
  }, [bandUsername]);

  // EPK handler functions
const handleEPKView = (epk) => {
  if (!epk || !epk.fileUrl) return;
  
  setSelectedAttachment({
    fileUrl: epk.fileUrl,
    fileName: epk.fileName || 'Electronic Press Kit',
    fileType: 'application/pdf'
  });
  setShowEPKModal(true);
};

const handleEPKUpload = async (file) => {
  if (!file || !bandData?.id) return;
  
  try {
    setIsEpkUploading(true);
    
    // Create form data for the upload
    const formData = new FormData();
    formData.append('media', file);
    
    // Upload the file with context=epk
    const uploadResponse = await fetch(`/api/processAndUploadMedia?context=epk&bandId=${bandData.id}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`
      },
      body: formData
    });
    
    if (!uploadResponse.ok) {
      throw new Error('Failed to upload EPK');
    }
    
    const uploadResult = await uploadResponse.json();
    
    // If successful, update the band document with the EPK info
    if (uploadResult.success) {
      const db = getFirestoreInstance();
      const bandRef = doc(db, 'bands', bandData.id);
      
      // If there's an existing EPK, prepare to delete it later
      const oldEPK = bandData?.epk;
      
      // Update the band document with the new EPK info
      await updateDoc(bandRef, {
        epk: {
          fileUrl: uploadResult.url,
          filePath: uploadResult.filePath,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          uploadedAt: new Date().toISOString(),
          uploadedBy: userData.userId
        }
      });
      
      // Delete the old file if it exists
      if (oldEPK?.filePath) {
        await fetch('/api/deleteBackblazeFile', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({ filePath: oldEPK.filePath })
        });
      }
      
      // Update the local state to reflect the changes
      setBandData(prev => ({
        ...prev,
        epk: {
          fileUrl: uploadResult.url,
          filePath: uploadResult.filePath,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          uploadedAt: new Date().toISOString(),
          uploadedBy: userData.userId
        }
      }));
      
      alert('EPK uploaded successfully!');
    }
  } catch (error) {
    console.error('Error uploading EPK:', error);
    alert('Failed to upload EPK. Please try again.');
  } finally {
    setIsEpkUploading(false);
  }
};

const handleEPKDelete = async (epk) => {
  if (!epk?.filePath || !bandData?.id) return;
  
  try {
    setIsEpkUploading(true);
    
    // Delete the file from storage
    const deleteResponse = await fetch('/api/deleteBackblazeFile', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ filePath: epk.filePath })
    });
    
    if (!deleteResponse.ok) {
      throw new Error('Failed to delete EPK file');
    }
    
    // Remove the EPK info from the band document
    const db = getFirestoreInstance();
    const bandRef = doc(db, 'bands', bandData.id);
    
    await updateDoc(bandRef, {
      epk: deleteField()
    });
    
    // Update the local state to reflect the changes
    setBandData(prev => {
      const { epk, ...rest } = prev;
      return rest;
    });
    
    alert('EPK removed successfully!');
  } catch (error) {
    console.error('Error deleting EPK:', error);
    alert('Failed to remove EPK. Please try again.');
  } finally {
    setIsEpkUploading(false);
  }
};

const handleOpenEPKBuilder = (mode = 'create') => {
  setEPKBuilderMode(mode);
  setShowEPKBuilder(true);
};

// Add useEffect to fetch custom EPK
useEffect(() => {
  // Function to fetch custom EPK data
  const fetchCustomEPK = async (bandId) => {
    try {
      console.log('Fetching custom EPK for band:', bandId);
      const db = getFirestoreInstance();
      const epkRef = doc(db, 'bands', bandId, 'epk', 'custom');
      const epkDoc = await getDoc(epkRef);
      
      if (epkDoc.exists()) {
        const epkData = epkDoc.data();
        console.log('Found custom EPK:', epkData);
        setCustomEPK(epkData);
      } else {
        console.log('No custom EPK found for band');
        setCustomEPK(null);
      }
    } catch (error) {
      console.error('Error fetching custom EPK:', error);
      setCustomEPK(null);
    }
  };

  if (bandData?.id) {
    fetchCustomEPK(bandData.id);
  }
}, [bandData?.id]);

// Function to save custom EPK
const handleSaveEPK = async (epkData, isPublishing) => {
  try {
    // Log the data being saved for debugging
    console.log('Saving EPK data:', epkData, 'isPublishing:', isPublishing);
    
    // Validate the data
    if (!epkData || !bandData?.id) {
      console.error('Missing required data for saving EPK', { epkData, bandId: bandData?.id });
      
      alert('Missing required information. Please try again.');
      return false;
    }
    
    const db = getFirestoreInstance();
    const epkRef = doc(db, 'bands', bandData.id, 'epk', 'custom');
    
    // Make sure we're not modifying the original data
    const dataToSave = {
      ...epkData,
      updatedAt: new Date().toISOString(),
      isActive: isPublishing ? true : (epkData.isActive || false)
    };
    
    // Log the actual data being written to Firestore
    console.log('Writing to Firestore:', dataToSave);
    
    // Update the document with the new EPK data
    await setDoc(epkRef, dataToSave);
    
    // Update local state
    setCustomEPK(dataToSave);
    
    // Close the EPK builder if publishing
    if (isPublishing) {
      setShowEPKBuilder(false);
    }
    
    // Show success message
    alert(isPublishing 
      ? "Your EPK has been published and is now live!" 
      : "Your EPK has been saved!");
    
    return true;
  } catch (error) {
    console.error('Error saving EPK:', error);
    
    // Show a more detailed error message
    alert(`Failed to save EPK: ${error.message || 'Unknown error'}. Please try again.`);
    
    return false;
  }
};

  const handlePitchDeckView = (pitchDeck) => {
    if (!pitchDeck || !pitchDeck.fileUrl) return;
    setSelectedAttachment({
      fileUrl: pitchDeck.fileUrl,
      fileName: pitchDeck.fileName || 'Band Pitch Deck',
      fileType: 'application/pdf'
    });
    setShowPitchDeckModal(true);
  };

  const handlePitchDeckUpload = async (file) => {
    if (!file || !bandData?.id || !isUserAdmin) return;
    try {
      setIsPitchDeckUploading(true);
      // Create form data for the upload
      const formData = new FormData();
      formData.append('media', file);
      
      // Upload the file with context=bandPitchDeck
      const uploadResponse = await fetch(`/api/processAndUploadMedia?context=bandPitchDeck&bandId=${bandData.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });
      
      if (!uploadResponse.ok) {
        throw new Error('Failed to upload pitch deck');
      }
      
      const uploadResult = await uploadResponse.json();
      
      // If successful, update the band document with the pitch deck info
      if (uploadResult.success) {
        const db = getFirestoreInstance();
        const bandRef = doc(db, 'bands', bandData.id);
        
        // If there's an existing pitch deck, prepare to delete it later
        const oldPitchDeck = bandData?.pitchDeck;
        
        // Update the band document with the new pitch deck info
        await updateDoc(bandRef, {
          pitchDeck: {
            fileUrl: uploadResult.url,
            filePath: uploadResult.filePath,
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            uploadedAt: new Date().toISOString(),
            uploadedBy: userData.userId
          }
        });
        
        // Delete the old file if it exists
        if (oldPitchDeck?.filePath) {
          await fetch('/api/deleteBackblazeFile', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({ filePath: oldPitchDeck.filePath })
          });
        }
        
        // Update the local state to reflect the changes
        setBandData(prev => ({
          ...prev,
          pitchDeck: {
            fileUrl: uploadResult.url,
            filePath: uploadResult.filePath,
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            uploadedAt: new Date().toISOString(),
            uploadedBy: userData.userId
          }
        }));
        
        // Show success message
        alert('Pitch deck uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading pitch deck:', error);
      alert('Failed to upload pitch deck. Please try again.');
    } finally {
      setIsPitchDeckUploading(false);
    }
  };

  const handlePitchDeckDelete = async (pitchDeck) => {
    if (!pitchDeck?.filePath || !bandData?.id || !isUserAdmin) return;
    try {
      setIsPitchDeckUploading(true);
      // Delete the file from storage
      const deleteResponse = await fetch('/api/deleteBackblazeFile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ filePath: pitchDeck.filePath })
      });
      
      if (!deleteResponse.ok) {
        throw new Error('Failed to delete pitch deck file');
      }
      
      // Remove the pitch deck info from the band document
      const db = getFirestoreInstance();
      const bandRef = doc(db, 'bands', bandData.id);
      
      await updateDoc(bandRef, {
        pitchDeck: deleteField()
      });
      
      // Update the local state to reflect the changes
      setBandData(prev => {
        const { pitchDeck, ...rest } = prev;
        return rest;
      });
      
      // Show success message
      alert('Pitch deck removed successfully!');
    } catch (error) {
      console.error('Error deleting pitch deck:', error);
      alert('Failed to remove pitch deck. Please try again.');
    } finally {
      setIsPitchDeckUploading(false);
    }
  };

  // Add the PitchDeckSection component for the band profile
  const BandPitchDeckSection = ({ bandData, isUserAdmin }) => {
    const fileInputRef = useRef(null);
    
    const triggerFileInput = () => {
      fileInputRef.current.click();
    };
    
    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size exceeds 5MB limit. Please select a smaller file.');
        return;
      }
      
      // Validate file type (only PDF)
      if (file.type !== 'application/pdf') {
        alert('Only PDF files are allowed.');
        return;
      }
      
      handlePitchDeckUpload(file);
    };
    
    return (
      <div className={styles.membersContainer}>
        <h3 className={styles.sectionTitle}>Band Pitch Deck</h3>
        {bandData?.pitchDeck ? (
          <div 
            className={styles.pitchDeckContainer}
            onClick={() => handlePitchDeckView(bandData.pitchDeck)}
          >
            <div className={styles.pitchDeckItem}>
              <FontAwesomeIcon icon={faFilePdf} className={styles.pitchDeckIcon} />
              <div className={styles.pitchDeckInfo}>
                <h4 className={styles.pitchDeckName}>{bandData.pitchDeck.fileName || 'Band Pitch Deck'}</h4>
                <p className={styles.pitchDeckNote}>Click to view</p>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>No pitch deck available</p>
            {isUserAdmin && (
              <p>Upload a PDF pitch deck to showcase your band to promoters and venues.</p>
            )}
          </div>
        )}
        
        {isUserAdmin && (
          <div className={styles.pitchDeckActions}>
            <input
              type="file"
              accept=".pdf"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            
            {bandData?.pitchDeck ? (
              <div className={styles.pitchDeckButtonGroup}>
                <button 
                  className={styles.uploadButton}
                  onClick={triggerFileInput}
                  disabled={isPitchDeckUploading}
                >
                  {isPitchDeckUploading ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <FontAwesomeIcon icon={faUpload} />
                  )}
                  Replace
                </button>
                
                <button 
                  className={`${styles.uploadButton} ${styles.deleteButton}`}
                  onClick={() => handlePitchDeckDelete(bandData.pitchDeck)}
                  disabled={isPitchDeckUploading}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Remove
                </button>
              </div>
            ) : (
              <button 
                className={styles.uploadButton}
                onClick={triggerFileInput}
                disabled={isPitchDeckUploading}
              >
                {isPitchDeckUploading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faPlus} />
                )}
                Add Pitch Deck
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  
  // Format date helper function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Determine if band is on tour
  const isOnTour = bandEvents.length > 0;
  
  // Handle profile sharing
  const handleShareProfile = () => {
    setShowShareModal(true);
  };

  // Handle join request
  const handleJoinRequest = async () => {
    if (!userData) {
      navigate('/login');
      return;
    }
    
    if (hasPendingRequest) {
      return; // Already has a pending request
    }
    
    try {
      await sendBandMembershipRequest(bandData.id, {
        userId: userData.userId,
        userName: userData.userName,
        fullName: userData.fullName,
        profilePictureUrl: userData.profilePictureUrl,
        userType: userData.userType
      });
      
      setHasPendingRequest(true);
      // Show a success message
      alert('Join request sent successfully!');
      
    } catch (error) {
      console.error('Error sending join request:', error);
      alert('Failed to send join request. Please try again.');
    }
  };

  // If loading
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading band profile...</p>
      </div>
    );
  }

  // If error or band not found
  if (error || !bandData) {
    return (
      <div className={styles.profileContainer}>
        <TopBar />
        <div className={styles.mainLayout}>
          <div className={styles.notFoundContainer}>
            <div className={styles.notFoundContent}>
              <div className={styles.notFoundArtwork}>
                <FontAwesomeIcon 
                  icon={faMusic} 
                  className={styles.notFoundIcon} 
                />
                <div className={styles.notFoundCircle}></div>
              </div>
              <h1>Band Not Found</h1>
              <p>Sorry, this band profile doesn't exist or may have been removed.</p>
              <button 
                className={styles.returnButton}
                onClick={() => navigate('/my-bands')}
              >
                <FontAwesomeIcon icon={faMusic} />
                My Bands
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.profileContainer}>
      <TopBar />
      <div className={styles.mainLayout}>
        {!isMobile && <LeftMenu />}
        {!isMobile && <RightMenu />}
        
        <div className={styles.profileMain}>
          <div className={styles.profileContent}>
            {/* Profile Card */}
            <div className={styles.profileCard}>
              <div className={styles.coverPhotoContainer}>
                <img 
                  src={bandData.bandCoverPictureUrl || defaultCoverPhoto}
                  alt="Cover"
                  className={styles.coverPhoto}
                />
                <div className={styles.coverOverlay}></div>
              </div>
              
              <div className={styles.profileHeader}>
                <div className={styles.profileLeft}>
                  <img
                    src={bandData.bandProfilePictureUrl || defaultProfilePhoto}
                    alt="Profile"
                    className={styles.profilePhoto}
                  />
                  
                  <div className={styles.profileInfo}>
                    <div className={styles.nameSection}>
                      <h1>{bandData.bandFullName}</h1>
                      {bandData.verified && (
                        <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                      )}
                    </div>

                    <div className={styles.userType}>
                      {bandData.userType}
                    </div>

                    <div className={styles.userType}>
                      {bandData.bandGenres && bandData.bandGenres.join(', ')}
                    </div>

                    <div className={styles.infoItems}>
                      {(bandData.city || bandData.state || bandData.country) && (
                        <div className={styles.infoItem}>
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                          <span>
                            {[
                              bandData.city, 
                              bandData.state, 
                              bandData.country
                            ].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      )}
                      
                      {/* Show On Tour or Available status based on current date */}
                      <div className={`${styles.infoItem} ${isOnTourStatus ? styles.onTour : styles.available}`}>
                        <FontAwesomeIcon icon={isOnTourStatus ? faRoute : faCalendarCheck} />
                        <span>{isOnTourStatus ? 'On Tour' : 'Available'}</span>
                      </div>
                    </div>

                    {/* Bio Section */}
                    {bandData.bandBio && (
                      <div className={styles.bioSection}>
                        <p className={styles.bio}>{bandData.bandBio}</p>
                      </div>
                    )}

                    {/* Social Media Icons */}
                    <div className={styles.socialIcons}>
                      {bandData.socialLinks?.instagramUrl && (
                        <a 
                          href={bandData.socialLinks.instagramUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className={styles.socialIconLink}
                        >
                          <FontAwesomeIcon icon={faInstagram} className={styles.socialIcon} />
                        </a>
                      )}
                      {bandData.socialLinks?.twitterUrl && (
                        <a 
                          href={bandData.socialLinks.twitterUrl}
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className={styles.socialIconLink}
                        >
                          <FontAwesomeIcon icon={faTwitter} className={styles.socialIcon} />
                        </a>
                      )}
                      {bandData.socialLinks?.facebookUrl && (
                        <a 
                          href={bandData.socialLinks.facebookUrl}
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className={styles.socialIconLink}
                        >
                          <FontAwesomeIcon icon={faFacebook} className={styles.socialIcon} />
                        </a>
                      )}
                      {bandData.socialLinks?.youtubeUrl && (
                        <a 
                          href={bandData.socialLinks.youtubeUrl}
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className={styles.socialIconLink}
                        >
                          <FontAwesomeIcon icon={faYoutube} className={styles.socialIcon} />
                        </a>
                      )}
                      {bandData.socialLinks?.spotifyUrl && (
                        <a 
                          href={bandData.socialLinks.spotifyUrl}
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className={styles.customSocialIcon}
                        >
                          <img src={spotifylogo} alt="Spotify" className={styles.socialIcon} />
                        </a>
                      )}
                      {bandData.socialLinks?.appleMusicUrl && (
                        <a 
                          href={bandData.socialLinks.appleMusicUrl}
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className={styles.customSocialIcon}
                        >
                          <img src={applemusiclogo} alt="Apple Music" className={styles.socialIcon} />
                        </a>
                      )}
                    </div>
                    
                    <div className={styles.profileActions}>
                      {userData ? (
                        <>
                          {isUserAdmin && (
                            <button 
                              onClick={() => navigate(`/bands/${bandData.id}/edit`)} 
                              className={`${styles.actionButton} ${styles.edit}`}
                            >
                              <FontAwesomeIcon icon={faUser} />
                              Edit Band
                            </button>
                          )}
                          
                          {!isUserMember && !hasPendingRequest && (
                            <button 
                              onClick={handleJoinRequest}
                              className={`${styles.actionButton} ${styles.join}`}
                            >
                              <FontAwesomeIcon icon={faUserPlus} />
                              Join Band
                            </button>
                          )}
                          
                          {!isUserMember && hasPendingRequest && (
                            <button 
                              disabled
                              className={`${styles.actionButton} ${styles.pending}`}
                            >
                              <FontAwesomeIcon icon={faClock} />
                              Request Pending
                            </button>
                          )}
                          
                          {bandData.bandEmail && (
                            <button 
                              onClick={() => window.location.href = `mailto:${bandData.bandEmail}`} 
                              className={`${styles.actionButton} ${styles.contact}`}
                            >
                              <FontAwesomeIcon icon={faEnvelope} />
                              Contact
                            </button>
                          )}
                        </>
                      ) : (
                        <button 
                          onClick={() => navigate('/login')} 
                          className={`${styles.actionButton} ${styles.login}`}
                        >
                          <FontAwesomeIcon icon={faSignInAlt} />
                          Log In to Interact
                        </button>
                      )}
                      
                      <button 
                        onClick={handleShareProfile} 
                        className={`${styles.actionButton} ${styles.share}`}
                      >
                        <FontAwesomeIcon icon={faShare} />
                        Share
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className={styles.profileRight}>
                  {/* Stats or additional information can go here */}
                  <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                      <div className={styles.statValue}>
                        {getActualMemberCount(bandData)}
                      </div>
                      <div className={styles.statLabel}>Members</div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={styles.statValue}>{bandEvents.filter(e => e.type === 'tour').length}</div>
                      <div className={styles.statLabel}>Tours</div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={styles.statValue}>
                        {bandEvents.filter(e => e.type === 'show').length + 
                         bandEvents.filter(e => e.type === 'tour').reduce((total, tour) => total + (tour.eventDates?.length || 0), 0)}
                      </div>
                      <div className={styles.statLabel}>Shows</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Members Section with updated conditional rendering */}
            <MembersSection 
              bandData={bandData} 
              isUserAdmin={isUserAdmin} 
              isUserMember={isUserMember} 
              navigate={navigate} 
            />

            {/* Add Band Pitch Deck Section here */}
            <BandEPKSection 
  bandData={bandData}
  isOwnProfile={isUserAdmin}
  handleEPKView={handleEPKView}
  handleEPKUpload={handleEPKUpload}
  handleEPKDelete={handleEPKDelete}
  openEPKBuilder={handleOpenEPKBuilder}
  customEPK={customEPK}
/>

            {/* Content Tabs */}
            <div className={styles.contentSection}>
              <div className={styles.contentTabs}>
                <button 
                  className={`${styles.tab} ${activeTab === 'showcase' ? styles.active : ''}`}
                  onClick={() => setActiveTab('showcase')}
                >
                  Showcase
                </button>
                <button 
                  className={`${styles.tab} ${activeTab === 'tourDates' ? styles.active : ''}`}
                  onClick={() => setActiveTab('tourDates')}
                >
                  Tour Dates
                </button>
              </div>

              <div className={styles.tabContent}>
                {activeTab === 'showcase' ? (
                  <div className={styles.showcaseSection}>
                    {isUserAdmin && (
                      <button className={styles.uploadButton}>
                        <FontAwesomeIcon icon={faPlus} />
                        Add to Showcase
                      </button>
                    )}
                    
                    {posts.length === 0 ? (
                      <div className={styles.emptyState}>
                        <FontAwesomeIcon icon={faMusic} className={styles.emptyIcon} />
                        <p>No content in showcase yet</p>
                        {isUserAdmin && (
                          <button className={styles.addFirstButton}>
                            Add First Post
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className={styles.postsGrid}>
                        {/* Posts would be mapped here */}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={styles.tourDatesSection}>
                    {isOnTour ? (
                      <TourDates bandData={bandData} events={bandEvents} />
                    ) : (
                      <div className={styles.emptyState}>
                        <FontAwesomeIcon icon={faCalendarCheck} className={styles.emptyIcon} />
                        <p>No upcoming tour dates</p>
                        {isUserAdmin && bandEvents.length === 0 && (
                          <p className={styles.adminTourHint}>
                            Assign an event to this band in the band settings
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {showToursModal && (
          <BandToursModal
            isOpen={showToursModal}
            onClose={() => setShowToursModal(false)}
            bandId={bandData.id}
          />
        )}

        {showShowsModal && (
          <BandShowsModal
            isOpen={showShowsModal}
            onClose={() => setShowShowsModal(false)}
            bandId={bandData.id}
          />
        )}    

        {showEPKModal && selectedAttachment && (
  <FileViewerModal
    isOpen={showEPKModal}
    onClose={() => setShowEPKModal(false)}
    file={selectedAttachment}
  />
)}

{showEPKBuilder && (
  <EPKBuilder
    isOpen={showEPKBuilder}
    onClose={() => setShowEPKBuilder(false)}
    userData={userData}
    mode={epkBuilderMode}
    existingEPK={customEPK}
    onSave={handleSaveEPK}
    authToken={authToken}
  />
)}

{customEPK && customEPK.isActive && (
  <EPKViewer
    isOpen={epkBuilderMode === 'view' && showEPKBuilder}
    onClose={() => setShowEPKBuilder(false)}
    epkData={customEPK}
    userName={bandData?.bandUserName}
  />
)}
        {showShareModal && (
          <ShareModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
            postData={{
              type: 'band',
              caption: bandData.bandBio || '',
              tags: bandData.bandGenres || [],
              postId: bandData.id,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default BandProfile;